import React, { useState, useRef, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { DynamicMesh } from './DynamicMesh';
import { STLExporter } from 'three/addons/exporters/STLExporter.js';
import * as THREE from 'three';








const defaultGeometryCode = `const meshes = [];

const gridSize = 9;
const spacing = 1;

const halfGrid = Math.floor(gridSize / 2);

for (let x = -halfGrid; x <= halfGrid; x++) {
    for (let y = -halfGrid; y <= halfGrid; y++) {
        for (let z = -halfGrid; z <= halfGrid; z++) {
            meshes.push({
                geometry: new SphereGeometry(0.3, 32, 16),
                color: "#00ff00",
                roughness: 0.1,
                metalness: 0.9,
                clearcoat: 1.0,
                position: [x * spacing, y * spacing, z * spacing]
            });
        }
    }
}

return meshes;`;

const chairCode = `const meshes = [];

// Seat
meshes.push({
    geometry: new BoxGeometry(1.5, 0.1, 1.5),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0, 0.5, 0]
});

// Backrest
meshes.push({
    geometry: new BoxGeometry(1.5, 1.0, 0.1),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0, 1.0, -0.7]
});

// Front Left Leg
meshes.push({
    geometry: new CylinderGeometry(0.05, 0.05, 1, 32),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [-0.7, 0, -0.7]
});

// Front Right Leg
meshes.push({
    geometry: new CylinderGeometry(0.05, 0.05, 1, 32),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0.7, 0, -0.7]
});

// Rear Left Leg
meshes.push({
    geometry: new CylinderGeometry(0.05, 0.05, 1, 32),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [-0.7, 0, 0.7]
});

// Rear Right Leg
meshes.push({
    geometry: new CylinderGeometry(0.05, 0.05, 1, 32),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0.7, 0, 0.7]
});

// Left Armrest
meshes.push({
    geometry: new BoxGeometry(0.1, 0.5, 1.5),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [-0.8, 0.75, 0]
});

// Right Armrest
meshes.push({
    geometry: new BoxGeometry(0.1, 0.5, 1.5),
    color: "#8B4513",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0.8, 0.75, 0]
});

return meshes;`;

const csgExampleCode = `const meshes = [];

// Create main box brush
const boxGeometry = new BoxGeometry(2, 2, 2);
const boxMaterial = new MeshPhysicalMaterial({ 
    color: "#00ff00",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2
});
const boxBrush = new Brush(boxGeometry, boxMaterial);

// Create hole brush (sphere)
const holeGeometry = new SphereGeometry(0.8, 32, 32);
const holeBrush = new Brush(holeGeometry, boxMaterial);
holeBrush.position.set(0, 0, 0);

// Perform CSG subtraction
const evaluator = new Evaluator();
const resultBrush = evaluator.evaluate(boxBrush, holeBrush, SUBTRACTION);

meshes.push({
    geometry: resultBrush.geometry,
    color: "#00ff00",
    roughness: 0.5,
    metalness: 0.3,
    clearcoat: 0.2,
    position: [0, 0, 0]
});

return meshes;`;

function App() {



const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
const [reasoningEffort, setReasoningEffort] = useState('medium');


useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, []);


    const [conversation, setConversation] = useState([
        {
            role: 'user',
            content: `You are a 3d modeling software, an assistant that generates Three.js geometry code based on user prompts. Follow these instructions carefully:

1. **Output Format**:
   - Your response must be a JavaScript function that returns an array of mesh configurations.
   - Each mesh configuration must include:
     - \`geometry\`: A Three.js geometry object (e.g., BoxGeometry, SphereGeometry).
     - \`color\`: A hex color string (e.g., "#00ff00").
     - \`roughness\`: A number between 0 and 1.
     - \`metalness\`: A number between 0 and 1.
     - \`clearcoat\`: A number between 0 and 1 (only if using MeshPhysicalMaterial).
     - \`position\`: An array of 3 numbers [x, y, z].
     - \`rotation\`: An array of 3 numbers [x, y, z] (optional).
     - \`scale\`: An array of 3 numbers [x, y, z] (optional).

2. **CSG Operations**:
   - Use the \`Brush\` class from \`three-bvh-csg\` to represent objects for CSG operations.
   - Use the \`Evaluator\` class to perform CSG operations like subtraction (\`SUBTRACTION\`), addition (\`ADDITION\`), and intersection (\`INTERSECTION\`).
   - Always use the imported \`SUBTRACTION\`, \`ADDITION\`, or \`INTERSECTION\` constants (do not use strings like "SUBTRACTION").
   - For CSG operations:
     - Create a new Evaluator instance for each operation
     - Ensure proper rotation and positioning of subtraction brushes
     - Make subtraction brushes slightly larger than needed to ensure clean cuts
     - Update matrixWorld after positioning brushes
   - Example for creating holes:
     \`\`\`
     const evaluator = new Evaluator();
     const holeBrush = new Brush(holeGeometry, material);
     holeBrush.position.set(x, y, z);
     holeBrush.rotation.set(Math.PI/2, 0, 0); // Rotate for proper orientation
     holeBrush.updateMatrixWorld();
     resultBrush = evaluator.evaluate(baseBrush, holeBrush, SUBTRACTION);
     \`\`\`

3. **Hole Creation Guidelines**:
   - When creating holes using cylinders:
     - Rotate Cylinders as needed
     - Make cylinder height greater than the thickness of the surface being cut
     - Position cylinders to fully penetrate the target geometry
     - Use fresh evaluator for each hole operation
     - Update matrixWorld after positioning
   - Example cylinder parameters:
     \`\`\`
     new CylinderGeometry(radius, radius, height, segments)
     \`\`\`
     Where height should be at least 2x the thickness of the surface

4. **Materials**:
   - Use \`MeshPhysicalMaterial\` if you need properties like \`clearcoat\`.
   - Use \`MeshStandardMaterial\` for standard materials.
   - Always use the \`new\` keyword when creating materials and geometries.

5. **Do Not Include**:
   - Import statements (e.g., \`import { ... } from 'three'\`).
   - Any code outside the function that returns the mesh configurations.

6. **Start and End**:
   - Every message should start with "const meshes = [];" and end with "return meshes;".
   - Do not declare the function, just the code inside.
   - Do not add commentary to the user.

7. **Example Output**:
   \`\`\`
   const meshes = [];

   // Create a plate with three holes
   const plateGeometry = new BoxGeometry(3, 0.2, 2);
   const plateMaterial = new MeshPhysicalMaterial({ 
       color: "#aaaaaa", 
       roughness: 0.8, 
       metalness: 0.1 
   });
   let plateBrush = new Brush(plateGeometry, plateMaterial);

   const holePositions = [-1.0, 0, 1.0];
   const holeRadius = 0.25;
   const holeHeight = 0.3;

   holePositions.forEach(xPos => {
       const evaluator = new Evaluator();
       const holeGeometry = new CylinderGeometry(holeRadius, holeRadius, holeHeight, 32);
       const holeBrush = new Brush(holeGeometry, plateMaterial);
       holeBrush.position.set(xPos, 0, 0);
       holeBrush.updateMatrixWorld();
       plateBrush = evaluator.evaluate(plateBrush, holeBrush, SUBTRACTION);
   });

   meshes.push({
       geometry: plateBrush.geometry,
       color: "#aaaaaa",
       roughness: 0.8,
       metalness: 0.1,
       position: [0, 0, 0]
   });

   return meshes;
   \`\`\`

Here is a list of Three JS "Geometries" you can use:
BoxGeometry
SphereGeometry
CylinderGeometry
ConeGeometry
TorusGeometry
TorusKnotGeometry
PlaneGeometry
CircleGeometry
ExtrudeGeometry
ShapeGeometry
TubeGeometry
PolyhedronGeometry
LatheGeometry
EdgesGeometry
RingGeometry
DodecahedronGeometry
IcosahedronGeometry
OctahedronGeometry
TetrahedronGeometry
TextGeometry`
        },
        { role: 'assistant', content: defaultGeometryCode },
        { role: 'user', content: 'A chair' },
        { role: 'assistant', content: chairCode },
        { role: 'user', content: 'A box with a hole using CSG' },
        { role: 'assistant', content: csgExampleCode }
    ]);
    
    const [input, setInput] = useState('');
    const [geometryCode, setGeometryCode] = useState(defaultGeometryCode);
    const [isLoading, setIsLoading] = useState(false);
    const [hasObjects, setHasObjects] = useState(false);
    const dynamicMeshRef = useRef();

    const sanitizeResponse = (response) => {
        const startIndex = response.indexOf('const meshes = []');
        const endIndex = response.lastIndexOf('return meshes;') + 'return meshes;'.length;
        return startIndex !== -1 && endIndex !== -1 ? response.substring(startIndex, endIndex) : response;
    };

    const detectErrors = (code) => {
        try {
            new Function(code);
            return null;
        } catch (error) {
            return error.message;
        }
    };

    const sendMessage = async (conversationHistory) => {
      try {
          const response = await fetch('/api/generate', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  conversation: conversationHistory,
                  reasoning_effort: reasoningEffort // Add this line
              })
          });
  
          if (!response.ok) throw new Error('API request failed');
          
          const data = await response.json();
          return data.message.content;
      } catch (error) {
          console.error('Error communicating with API:', error);
          return 'Error generating geometry code';
      }
  };
      

    const handleDownload = () => {
        const group = dynamicMeshRef.current?.getGroup();
        if (!group) return;

        const exportGroup = new THREE.Group();
        
        group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                const geometry = child.geometry.clone();
                geometry.applyMatrix4(child.matrixWorld);
                
                const mesh = new THREE.Mesh(geometry, child.material);
                exportGroup.add(mesh);
            }
        });

        const exporter = new STLExporter();
        const stlString = exporter.parse(exportGroup, { binary: true });
        
        const blob = new Blob([stlString], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'model.stl';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleSubmit = async (e) => {
      if (e.key === 'Enter' && input.trim() !== '') {
          e.preventDefault();
          const userMessage = input.trim();
          const updatedConversation = [...conversation, { role: 'user', content: userMessage }];
          
          setConversation(updatedConversation);
          setInput('');
          setIsLoading(true);
  
          try {
              const assistantResponse = await sendMessage(updatedConversation);
              const sanitizedCode = sanitizeResponse(assistantResponse);
              const error = detectErrors(sanitizedCode);
              
              setConversation(prev => [...prev, { role: 'assistant', content: sanitizedCode }]);
              if (!error) setGeometryCode(sanitizedCode);
              
          } catch (error) {
              console.error('Error:', error);
          } finally {
              setIsLoading(false);
          }
      }
  };


    return (
        <div style={{ 
          position: 'fixed', // Changed from relative to fixed
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: '#dbd4ca',
          overflow: 'hidden'
        }}>
          {hasObjects && (
            <button
              onClick={handleDownload}
              style={{
                position: 'absolute',
                top: isMobile ? '25px' : '15px',
                right: '10px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                border: '2px solid rgba(255, 255, 255, 0.6)',
                color: '#333',
                borderRadius: '25px',
                cursor: 'pointer',
                backdropFilter: 'blur(10px)',
                zIndex: 1000,
              }}
            >
              Download STL
            </button>
          )}
      
          <Canvas 
            style={{
              width: '100vw',
              height: '100vh',
              touchAction: 'none'
            }}
            camera={{ position: [15, 15, 15], fov: 45, near: 0.1, far: 1000 }}
          >
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, 10, 10]} intensity={1.5} castShadow />
            <OrbitControls 
              enableDamping 
              dampingFactor={0.1}
              enablePan={!isMobile}
              enableZoom={true}
            />
            <DynamicMesh
              geometryCode={geometryCode}
              ref={dynamicMeshRef}
              onMeshConfigsGenerated={(hasObjs) => setHasObjects(hasObjs)}
            />
          </Canvas>
       {/* Reasoning Effort Slider */}
<div
  style={{
    position: 'absolute',
    bottom: isMobile ? '105px' : '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 40px)',
    maxWidth: '500px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    borderRadius: '25px',
    backdropFilter: 'blur(10px)',
    color: '#333',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 999,
    boxSizing: 'border-box',
  }}
>
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      textAlign: 'center',
    }}
  >
    <div style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.8 }}>
      {reasoningEffort.toUpperCase()} EFFORT
    </div>
    <input
      type="range"
      min="0"
      max="2"
      step="1"
      value={['low', 'medium', 'high'].indexOf(reasoningEffort)}
      onChange={(e) => {
        const efforts = ['low', 'medium', 'high'];
        setReasoningEffort(efforts[e.target.value]);
      }}
      className="slider"
    />
  </div>
  <style>{`
    .slider {
      -webkit-appearance: none;
      appearance: none;
      width: 100%;
      height: 2px;
      border-radius: 1px;
      background: rgba(0, 0, 0, 0.1);
      outline: none;
      cursor: pointer;
    }
    .slider::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #ffffff; /* White thumb */
      border: 1px solid rgba(0, 0, 0, 0.1);
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
    .slider::-moz-range-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #ffffff; /* White thumb */
      border: 1px solid rgba(0, 0, 0, 0.1);
      cursor: pointer;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }
  `}</style>
</div>

<input
  type="text"
  placeholder={isLoading ? "Generating..." : "Describe a 3D object..."}
  value={input}
  onChange={(e) => setInput(e.target.value)}
  onKeyDown={handleSubmit}
  disabled={isLoading}
  style={{
    position: 'absolute',
    bottom: isMobile ? '180px' : '110px', // Adjusted for slider
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'calc(100% - 40px)', // Responsive width with margins
    maxWidth: '500px', // Maximum width for desktop
    padding: '16px 20px', // Increased padding for bigger size
    fontSize: '16px', // Slightly larger font
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
    borderRadius: '25px',
    backdropFilter: 'blur(10px)',
    color: '#333',
    outline: 'none',
    transition: 'all 0.3s ease',
    fontWeight: 'bold',
    textAlign: 'center',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 999,
    boxSizing: 'border-box'
  }}
/>
        </div>
      );
      
      
      
    
    
}    

export default App;